export type { Question, Vocabulary, SpeakingGuide, TopicDraft, ProgressStep, ProgressStepName } from "@/lib/topic/types";

// Import for local use in the stream-event interfaces below.
import type { Question, Vocabulary, SpeakingGuide, TopicDraft, ProgressStep } from "@/lib/topic/types";

/**
 * Which surface owns an `ai_conversations` row. `aurora` is the /my-topics/ai
 * chat; `assistant` is the docked widget behind the bottom-right FAB. Aurora's
 * sidebar and the widget's history both filter on this so the two stores stay
 * readable as separate lists inside one table.
 */
export type ConversationKind = "aurora" | "assistant";

/** Aurora owns the sidebar; everything else is out of scope for it. */
export const AURORA_CONVERSATION_KIND: ConversationKind = "aurora";
export const ASSISTANT_CONVERSATION_KIND: ConversationKind = "assistant";

/**
 * Streaming status of the assistant turn currently on screen.
 * `thinking` = no text yet, `generating` = partial text, `result` = done.
 */
export type AIStatus = "thinking" | "generating" | "result";

export interface ConversationSummary {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
}

/**
 * The practice question the reader is looking at, in assistant mode. Drives the
 * "Current Practice Context" block of the assistant system prompt — it is what
 * makes the widget answer about *this* question instead of language in general.
 */
export interface AssistantQuestionContext {
  questionText: string;
  difficulty?: string;
  keyPoints?: string[];
  stepByStep?: string[];
  exampleAnswer?: string;
  sessionTopic?: string;
  vocabulary?: Array<{ word: string; definition?: string }>;
}

/** Page context a widget request may carry alongside its message. */
export interface AssistantRequestContext {
  currentRoute?: string;
  questionContext?: AssistantQuestionContext;
}

/** How `ai-chat` should answer: Aurora's topic builder, or the widget's coach. */
export type AIChatMode = "topic" | "assistant";

export interface WebSearchPart {
  type: "data-web-search";
  data: WebSearchResult;
}

export interface UIMessagePart {
  type: string;
  text?: string;
  data?: unknown;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export interface StoredConversationMessage {
  id: string;
  role: "user" | "assistant" | "topic-draft" | "web-search" | "url-content" | "system";
  content: string;
  parts?: UIMessagePart[];
  timestamp?: string;
  modelId?: string;
  sessionId?: string;
  kind?: SystemMessageKind;
}

/**
 * A document attachment uploaded to Cloudinary via upload-chat-attachment.
 * `extractedText` is the readable text fed to the model (folded into the user
 * message's wire text); it is persisted so context survives a page reload.
 */
export interface ChatAttachment {
  url: string;
  publicId: string;
  filename: string;
  mimeType: string;
  bytes: number;
  extractedText: string;
  truncated: boolean;
}

export interface AttachmentPart {
  type: "data-attachment";
  data: ChatAttachment;
}

/**
 * Interactive clarifying Q&A card surfaced when the user's topic request is
 * broad or missing key context (scenario, focus area, proficiency level).
 */
export interface ClarifyingQuestionOption {
  label: string;
  description?: string;
}

export interface ClarifyingQuestionItem {
  id: string;
  question: string;
  options: ClarifyingQuestionOption[];
  allowMultiple?: boolean;
}

export interface ClarifyingQuestionsData {
  title?: string;
  questions: ClarifyingQuestionItem[];
}

export interface ClarifyingQuestionsPart {
  type: "data-clarifying-questions";
  data: ClarifyingQuestionsData;
}

export interface StreamClarifyingQuestionsEvent {
  type: "clarifying-questions";
  data: ClarifyingQuestionsData;
}

export interface ConversationDetail {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
  user_id?: string;
  messages: StoredConversationMessage[];
  deleted_at?: string | null;
}

/** When / how a preference field was last written (auto-learned vs manual). */
export interface PreferenceProvenance {
  at: string;
  conversation_id: string | null;
  via: "auto" | "manual";
}

/** Auto-learned learner preferences stored in ai_user_preferences. */
export interface AiUserPreferences {
  native_language: string | null;
  english_level: string;
  interests: string[];
  practice_goal: string | null;
  /** Per-field provenance (absent = pre-existing rows without metadata). */
  provenance?: Partial<Record<PreferenceField, PreferenceProvenance>>;
}

export type PreferenceField = "native_language" | "english_level" | "interests" | "practice_goal";

/** A learning/save event for the "Recent learning" feed. */
export interface PreferenceEvent {
  id: string;
  conversation_id: string | null;
  fields: string[];
  summary: string;
  via: "auto" | "manual";
  created_at: string;
}

/**
 * How a `system` message should read. Persisted alongside `content` so the UI
 * does not have to infer intent from the message text (a user stop and a
 * server failure can otherwise look identical).
 */
export type SystemMessageKind = "error" | "interrupted";

export interface ChatMessage {
  id: string;
  role: "user" | "assistant" | "topic-draft" | "web-search" | "url-content" | "system" | "preferences";
  content: string;
  parts?: UIMessagePart[];
  createdAt: Date;
  modelId?: string;
  sessionId?: string;
  /** Only meaningful on `system` messages; see {@link SystemMessageKind}. */
  kind?: SystemMessageKind;
}

export interface StreamTextEvent {
  type: "text";
  content: string;
}

export interface StreamProgressEvent {
  type: "progress";
  progress: ProgressStep;
}

export interface StreamTopicDraftEvent {
  type: "topic-draft";
  draft: TopicDraft;
}

export interface StreamFinishEvent {
  type: "finish";
  payload: unknown;
}

export interface StreamErrorEvent {
  type: "error";
  message: string;
}

export interface WebSearchResultItem {
  position: number;
  site_name: string;
  title: string;
  snippet: string;
  url: string;
}

export type WebSearchStatus = "success" | "empty" | "error";
export type WebSearchErrorCode =
  | "upstream_unauthorized"
  | "upstream_rate_limited"
  | "upstream_error"
  | "internal";

export interface WebSearchResult {
  query: string;
  results: WebSearchResultItem[];
  total_results: number;
  status?: WebSearchStatus;
  errorCode?: WebSearchErrorCode;
  errorMessage?: string;
}

export interface StreamWebSearchEvent {
  type: "web-search";
  search: WebSearchResult;
}

export interface StreamWebSearchPendingEvent {
  type: "web-search-pending";
  query: string;
  toolCallId: string;
}

export type UrlContentStatus = "success" | "empty" | "error";

/** Compact fetched-link result surfaced to the UI as a `data-url-content` part. */
export interface UrlContentResult {
  url: string;
  title?: string;
  siteName?: string;
  byline?: string;
  status: UrlContentStatus;
  truncated?: boolean;
  excerpt?: string;
  /** Persisted page text (capped) so the agent keeps the link's context after a reload. */
  text?: string;
  errorMessage?: string;
}

export interface UrlContentPart {
  type: "data-url-content";
  data: UrlContentResult;
}

export interface UrlContentPendingPart {
  type: "data-url-content-pending";
  data: { url: string; toolCallId: string };
}

export interface StreamUrlContentEvent {
  type: "url-content";
  content: UrlContentResult;
}

export interface StreamUrlContentPendingEvent {
  type: "url-content-pending";
  url: string;
  toolCallId: string;
}

export interface StreamTopicContentEvent {
  type: "topic-content";
  data: {
    title?: string;
    description?: string;
    category: string;
    bannerUrl?: string;
    bannerAlt?: string;
    questions: Question[];
    vocabulary: Vocabulary[];
    speakingGuide: SpeakingGuide;
  };
}

export interface StreamPreferencesUpdatedEvent {
  type: "preferences-updated";
  preferences: AiUserPreferences;
}

export interface StreamConversationTitleEvent {
  type: "conversation-title";
  title: string;
}

export type AIChatStreamEvent =
  | StreamTextEvent
  | StreamProgressEvent
  | StreamTopicDraftEvent
  | StreamTopicContentEvent
  | StreamFinishEvent
  | StreamErrorEvent
  | StreamWebSearchEvent
  | StreamWebSearchPendingEvent
  | StreamUrlContentEvent
  | StreamUrlContentPendingEvent
  | StreamClarifyingQuestionsEvent
  | StreamPreferencesUpdatedEvent
  | StreamConversationTitleEvent;
